import View                          from '@widesk-ui/components/View';
import { CopyOutlined }              from '@ant-design/icons';
import { SettingOutlined }           from '@ant-design/icons';
import SwitchField                   from '@/TokamapEditor/ux/SwitchField';
import currentEditorStore            from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore     from '@/TokamapEditor/stores/currentProjectEditorStore';
import { observer }                  from 'mobx-react-lite';
import Field                         from '@/TokamapEditor/ux/AreaSidebar/Field';
import ColorButton                   from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import SectionTitle                  from '@/TokamapEditor/ux/AreaSidebar/SectionTitle';
import CollapsiblePanel              from '@/TokamapEditor/ux/AreaSidebar/CollapsiblePanel';
import FunctionalityGate             from '@/components/Functionality';
import { TileLayerName }             from '@tokamap/core';
import { OsmStyleName }              from '@tokamap/core';
import { resolveOsmOptions }         from '@tokamap/core';
import type { RenderingAmbiance }    from '@tokamap/core';
import Segmented                     from '@widesk-ui/Segmented';
import React                        from 'react';
import useForceUpdate                from '@widesk-core/hooks/useForceUpdate';
import useDesignTokens               from '@/hooks/useDesignTokens';
import Select                        from '@widesk-ui/components/form/Select';
import { Input }                     from '@widesk-ui/components/form/Input';
import publicProjectLink             from '@/tools/publicProjectLink';
import { message }                   from '@widesk-ui/hooks/useMessage';
import FramingSection                from '@/TokamapEditor/ux/Framing/FramingSection';
import SliderInputNumber             from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import { LENGTH_UNITS }              from '@/TokamapEditor/helpers/mapKind';
import { GRID_STEPS }                from '@/TokamapEditor/helpers/mapKind';
import { defaultGridStep }           from '@/TokamapEditor/helpers/mapKind';
import { formatLength }              from '@/TokamapEditor/helpers/mapKind';
import type { LengthUnit }           from '@/TokamapEditor/helpers/mapKind';
import { SHOW_DECOR }                from '@/TokamapEditor/ux/constants';

export function SettingsSwatch() {
	const tokens = useDesignTokens();

	return (
		<View
			w={46}
			h={46}
			br={8}
			center
			color={tokens.color.primary}
			style={{ background: tokens.surface.subtle, border: `1px solid ${tokens.border.default}`, flexShrink: 0 }}
		>
			<SettingOutlined style={{ fontSize: 22 }} />
		</View>
	);
}

const SettingsSidebar = observer(() => {
	const editor = currentEditorStore.get;
	const projectStore = currentProjectEditorStore.get;
	const tokens = useDesignTokens();

	React.useEffect(() => {
		if (!editor) return;

		return editor.mapAdapter.on((event) => {
			if (
				event.type !== 'geolocation:changed'
				&& event.type !== 'allow-pitch:changed'
				&& event.type !== 'tile-layer:changed'
				&& event.type !== 'osm-options:changed'
				&& event.type !== 'rendering-options:changed'
				&& event.type !== 'background-color:changed'
			) return;
			forceUpdate();
		});
	}, [editor]);

	const forceUpdate = useForceUpdate();
	const isFreePlan = currentEditorStore.isFreePlan;

	// L'adresse publique appartient au PROJET : elle sert toutes ses cartes, pas seulement celle-ci.
	const publicLink = publicProjectLink(projectStore.project);

	return (
		<View p="md" gap="md">
			<View gap="md">
				<SectionTitle>Cadrage</SectionTitle>

				<FramingSection />
			</View>

			<View h={1} bg={tokens.border.default} />

			<View gap="md">
				<SectionTitle>{isFreePlan ? 'Plan' : 'Carte de fond'}</SectionTitle>

				{isFreePlan && (
					<Field label="Unité de mesure" help="change l'affichage des cotes et des saisies, les formes ne bougent pas.">
						<Select
							value={currentEditorStore.lengthUnit}
							onChange={value => {
								const unit = value as LengthUnit;
								const step = currentEditorStore.gridStep;

								currentEditorStore.setLengthUnit(unit);
								if (step && !GRID_STEPS[unit].includes(step)) currentEditorStore.setGridStep(defaultGridStep(unit));
								currentEditorStore.saveMapKindSettings();
							}}
							options={LENGTH_UNITS}
						/>
					</Field>
				)}

				{isFreePlan && (
					<SwitchField
						label="Règles graduées"
						help="en haut et à gauche de la carte, avec la position du curseur."
						value={currentEditorStore.rulers}
						onChange={value => {
							currentEditorStore.setRulers(value);
							currentEditorStore.saveMapKindSettings();
						}}
					/>
				)}

				{isFreePlan && (
					<SwitchField
						label="Grille magnétique"
						help="avec la touche Maj, les points se posent sur la grille quand aucune forme n'est à portée."
						value={!!currentEditorStore.gridStep}
						onChange={value => {
							currentEditorStore.setGridStep(value ? defaultGridStep(currentEditorStore.lengthUnit) : null);
							currentEditorStore.saveMapKindSettings();
						}}
					/>
				)}

				{isFreePlan && !!currentEditorStore.gridStep && (
					<Field label="Pas de la grille" help="en dézoomant, la grille affichée s'espace par dix pour rester lisible, et le magnétisme suit ce qui est affiché.">
						<Select
							value={currentEditorStore.gridStep}
							onChange={value => {
								currentEditorStore.setGridStep(Number(value));
								currentEditorStore.saveMapKindSettings();
							}}
							options={GRID_STEPS[currentEditorStore.lengthUnit].map(step => ({
								value: step,
								label: formatLength(step, currentEditorStore.lengthUnit),
							}))}
						/>
					</Field>
				)}

				{!isFreePlan && (
					<FunctionalityGate reference="geolocalisation" fullWidth>
						<SwitchField
							label="Activer la géolocalisation"
							help="affiche un bouton pour centrer la carte sur la position de l'utilisateur et permet de l'utiliser comme point de départ d'un itinéraire."
							value={editor.getGeolocationEnabled()}
							onChange={value => editor.setGeolocationEnabled(value)}
						/>
					</FunctionalityGate>
				)}

				{/* Hors des options du fond OpenStreetMap : la 3D vaut aussi pour les volumes des formes, quel que soit le fond. */}
				<SwitchField
					label="Activer la 3D"
					help="autorise l'inclinaison de la caméra pour afficher la carte et les volumes des formes en perspective."
					value={editor.getAllowPitch()}
					onChange={value => {
						// `skipHistory`/`skipSave` fait porter les deux changements par la SEULE commande qui
						// suit (voir `ConfigurationSaveTracker.resolveHistoryBeforeConfiguration`) : un unique
						// Annuler les rétablit ensemble.
						if (!value) editor.setOsmOptions({ buildings3d: false }, { skipHistory: true, skipSave: true });
						editor.setAllowPitch(value);
					}}
				/>

				{editor.getAllowPitch() && <RenderingSection />}

				<SwitchField
					label="Vol d'arrivée"
					help="à l'ouverture, la caméra arrive d'une vue large et plonge vers la vue de départ. Un geste du visiteur l'interrompt."
					value={editor.mapAdapter.getEffectiveRenderingOptions().arrivalFlight}
					onChange={value => editor.setRenderingOptions({ arrivalFlight: value })}
				/>

				<SwitchField
					label="Mise en lumière"
					help="un stand trouvé par la recherche ou dont on ouvre la fiche reste éclairé, le reste de la carte s'assombrit."
					value={editor.mapAdapter.getEffectiveRenderingOptions().spotlight}
					onChange={value => editor.setRenderingOptions({ spotlight: value })}
				/>

				<Field label="Couleur de fond">
					<View row>
						<ColorButton
							label="Fond de la carte"
							value={editor.getBackgroundColor()}
							onChange={color => editor.setBackgroundColor(color)}
						/>
					</View>
				</Field>

				{!isFreePlan && (
					<Field label="Carte du monde">
						<Select
							value={editor.getTileLayerName()}
							onChange={value => editor.setTileLayerName((value || undefined) as TileLayerName)}
							options={tileLayerOptions}
						/>
					</Field>
				)}

				{!isFreePlan && editor.getTileLayerName() === 'osm' && <AdvancedOptionsSection />}
			</View>

			<View h={1} bg={tokens.border.default} />

			<View gap="md">
				<SectionTitle>Partage</SectionTitle>

				<Field label="Lien public">
					<Input
						value={publicLink}
						disabled
						suffix={(
							<CopyOutlined
								onClick={async () => {
									await navigator.clipboard.writeText(publicLink);
									message.open({ type: 'success', content: 'Lien copié dans le presse-papier.', duration: 2 });
								}}
							/>
						)}
					/>
				</Field>
			</View>
		</View>
	);
});

export default SettingsSidebar;

const tileLayerOptions: {
	label: string;
	value: TileLayerName | '';
}[] = [
	{
		label: 'Ne pas afficher',
		value: 'unset',
	},
	{
		label: 'Open street map',
		value: 'osm',
	},
	{
		label: 'Satellite',
		value: 'satellite',
	},
];

/**
 * Réglages propres au fond OpenStreetMap, rendus à l'intérieur du choix dont ils dépendent (voir
 * `MapForm`, même parti pris). Chaque contrôle n'écrit qu'un champ (`setOsmOptions` fusionne) mais
 * lit la valeur EFFECTIVE via `resolveOsmOptions` : un réglage jamais touché est absent de la
 * configuration, alors qu'un interrupteur a besoin d'un booléen.
 */
// Pas d'`observer` : le `React.memo` qu'il pose bloquerait le `forceUpdate` du parent, seul mécanisme
// de rafraîchissement ici — l'adapter n'est pas observable.
function AdvancedOptionsSection() {
	const editor = currentEditorStore.get;
	const [fadeDraft, setFadeDraft] = React.useState<number>();

	const options = resolveOsmOptions(editor.getOsmOptions());
	const allowPitch = editor.getAllowPitch();

	return (
		<CollapsiblePanel title="Options avancées Open street map">
			<Field label="Style du fond">
				<Segmented
					block
					size="small"
					value={options.style}
					onChange={value => editor.setOsmOptions({ style: value as OsmStyleName })}
					options={osmStyleOptions}
				/>
			</Field>

			<Field
				label="Langue des libellés"
				help="un lieu sans nom dans cette langue garde son nom local."
			>
				<Select
					value={options.language ?? LOCAL_LANGUAGE_VALUE}
					onChange={value => editor.setOsmOptions({
						// `null`, pas `undefined` : voir `OsmBackgroundOptions.language`.
						language: value === LOCAL_LANGUAGE_VALUE ? null : (value as string),
					})}
					options={osmLanguageOptions}
				/>
			</Field>

			<Field label="Atténuation du fond" help="éclaircit le fond pour faire ressortir le plan.">
				<SliderInputNumber
					min={0}
					max={100}
					step={5}
					suffix="%"
					value={fadeDraft ?? Math.round(options.fade * 100)}
					onChange={value => setFadeDraft(value)}
					onAfterChange={value => {
						setFadeDraft(undefined);
						editor.setOsmOptions({ fade: (value ?? 0) / 100 });
					}}
				/>
			</Field>

			<Field label="Éléments affichés">
				<View gap="xs">
					<SwitchField
						label="Routes"
						help="rues, chemins et voies ferrées."
						value={options.roads}
						onChange={value => editor.setOsmOptions({ roads: value })}
					/>

					<SwitchField
						label="Métro et voies ferrées"
						help="métro, RER, trains et tramways."
						value={options.roads && options.railways}
						disabled={!options.roads}
						onChange={value => editor.setOsmOptions({ railways: value })}
					/>

					<SwitchField
						label="Voies souterraines"
						help="routes et voies ferrées qui passent en tunnel."
						value={options.roads && options.tunnels}
						disabled={!options.roads}
						onChange={value => editor.setOsmOptions({ tunnels: value })}
					/>

					<SwitchField
						label="Cheminements intérieurs"
						help="couloirs et escaliers à l'intérieur des bâtiments, tous niveaux."
						value={options.roads && options.indoor}
						disabled={!options.roads}
						onChange={value => editor.setOsmOptions({ indoor: value })}
					/>

					<SwitchField
						label="Eau"
						help="mers, lacs, rivières et leurs noms."
						value={options.water}
						onChange={value => editor.setOsmOptions({ water: value })}
					/>

					<SwitchField
						label="Végétation"
						help="parcs, bois, pelouses."
						value={options.vegetation}
						onChange={value => editor.setOsmOptions({ vegetation: value })}
					/>

					<SwitchField
						label="Bâtiments"
						help="masqués, ils restent disponibles pour la baguette magique."
						value={options.buildings}
						onChange={value => editor.setOsmOptions({ buildings: value })}
					/>
				</View>
			</Field>

			<Field label="Libellés affichés">
				<View gap="xs">
					<SwitchField
						label="Noms de lieux"
						help="villes, quartiers, pays, mers, aéroports."
						value={options.placeLabels}
						onChange={value => editor.setOsmOptions({ placeLabels: value })}
					/>

					<SwitchField
						label="Noms de rues"
						help={options.roads
							? 'voies, numéros de route, sens uniques.'
							: 'masqués avec les routes.'}
						value={options.roads && options.roadLabels}
						disabled={!options.roads}
						onChange={value => editor.setOsmOptions({ roadLabels: value })}
					/>

					<SwitchField
						label="Points d'intérêt"
						help={options.style === 'detailed'
							? 'parkings, commerces, restaurants, arrêts…'
							: 'seulement en vue détaillée.'}
						value={options.style === 'detailed' && options.poiLabels}
						disabled={options.style !== 'detailed'}
						onChange={value => editor.setOsmOptions({ poiLabels: value })}
					/>
				</View>
			</Field>

			<SwitchField
				label="Bâtiments en relief"
				help={!allowPitch
					? 'nécessite « Activer la 3D », ci-dessus.'
					: options.buildings
						? 'donne du volume au bâti ; visible en inclinant la caméra.'
						: 'masqué avec les bâtiments.'}
				value={options.buildings && options.buildings3d}
				disabled={!allowPitch || !options.buildings}
				onChange={value => editor.setOsmOptions({ buildings3d: value })}
			/>
		</CollapsiblePanel>
	);
}

/** Ombres et lumière de la vue en 3D : les ombres, et une ambiance prête à l'emploi. */
// Pas d'`observer`, pour la même raison qu'`AdvancedOptionsSection`.
function RenderingSection() {
	const editor = currentEditorStore.get;
	const options = editor.mapAdapter.getEffectiveRenderingOptions();

	return (
		<CollapsiblePanel title="Ombres et lumière">
			<SwitchField
				label="Ombres des volumes"
				help="une ombre douce sous les stands et les murs, étirée à l'opposé du soleil."
				value={options.contactShadows}
				onChange={value => editor.setRenderingOptions({ contactShadows: value })}
			/>

			{SHOW_DECOR && (
				<SwitchField
					label="Ombres des objets 3D"
					help="sous le mobilier, les arbres, les véhicules et les personnages."
					value={options.decorShadows}
					onChange={value => editor.setRenderingOptions({ decorShadows: value })}
				/>
			)}

			<Field label="Ambiance">
				<Segmented
					block
					size="small"
					value={options.ambiance}
					onChange={value => editor.setRenderingOptions({ ambiance: value as RenderingAmbiance })}
					options={ambianceOptions}
				/>
			</Field>
		</CollapsiblePanel>
	);
}

const ambianceOptions: {
	label: string;
	value: Exclude<RenderingAmbiance, 'custom'>;
}[] = [
	{ label: 'Douce', value: 'soft' },
	{ label: 'Midi', value: 'noon' },
	{ label: 'Soir', value: 'evening' },
	{ label: 'Nuit', value: 'night' },
	{ label: 'Contraste', value: 'contrast' },
];

const osmStyleOptions: {
	label: string;
	value: OsmStyleName;
}[] = [
	{ label: 'Clair', value: 'light' },
	{ label: 'Sombre', value: 'dark' },
	{ label: 'Sobre', value: 'sober' },
	{ label: 'Détaillé', value: 'detailed' },
];

/** `Select` ne sait pas porter `null` comme valeur d'option. */
const LOCAL_LANGUAGE_VALUE = '';

/** Codes OSM (`name:<langue>`) — toute langue présente dans OSM fonctionnerait, la liste est courte
 * par choix. */
const osmLanguageOptions: {
	label: string;
	value: string;
}[] = [
	{ label: 'Nom local', value: LOCAL_LANGUAGE_VALUE },
	{ label: 'Français', value: 'fr' },
	{ label: 'English', value: 'en' },
	{ label: 'Deutsch', value: 'de' },
	{ label: 'Español', value: 'es' },
	{ label: 'Italiano', value: 'it' },
	{ label: 'Nederlands', value: 'nl' },
	{ label: 'Português', value: 'pt' },
	{ label: 'Русский', value: 'ru' },
	{ label: '日本語', value: 'ja' },
	{ label: '中文', value: 'zh' },
	{ label: 'العربية', value: 'ar' },
];
